import { CircularProgress, Grid, Stack } from "@mui/material";
import { useTheme } from "styled-components";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import AdDrawerHeader from "./adDrawerHeader";
import { StyledDivider } from "../../../globalStyle";
import AdCardSection from "./adCardSection";
import TranscriptionSection from "./transcriptionSection";
import { AdDrawerStyledButton, StyledGridItem } from "./style";
import OrganizationBox from "./organizationBox";
import MetadataBox from "./metadataBox";
import CommentBox from "./commentBox";
import { SwipeFileReducerAction } from "../../../store/swipeFile/slice";
import { MetaDataSagaActions } from "../../../store/metadata/sagas";
import {
  adDetailsSelector,
  downloadMediaLoaderSelector, isAdDetailsError,
  isLoadingAdDetails,
  openCreateFolderSelector,
  openCreateTeamFolderSelector,
} from "../../../store/metadata/selector";
import { discoveryTabIndexSelector } from "../../../store/discovery/selector";
import { useEffect } from "react";
import { SwipeFileSagaActions } from "../../../store/swipeFile/sagas";
import DeleteAdModal from "../../modals/deleteAdModal";
import CreateFolderModal from "../../modals/createFolderModal";
import routeConstant from "../../../utils/routeConstant";
import CreateTeamFolderModal from "../../modals/createTeamFolderModal";
import { userDetailsSelector } from "../../../store/account/selector";
import { Box } from "@mui/system";
import { MetaDataReducerAction } from "../../../store/metadata/slice";
import { notify, ToastTypes } from "../../../utils/helpers";

const AdDetails = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const openCreateFolder = useSelector(openCreateFolderSelector);
  const openCreateTeamFolder = useSelector(openCreateTeamFolderSelector);
  const user = useSelector(userDetailsSelector);

  let { libraryId, categoryType } = useParams();

  const isSwipeFile =
    categoryType === "swipe-ad" || categoryType === "swipe-ads";

  const isNotExploreLounge =
    window.location.pathname.split("/")[1] !== "explore-lounge";

  const discoveryTabIndex = useSelector(discoveryTabIndexSelector);
  const isExploreDiscovery = 
    categoryType === "explore-lounge" && discoveryTabIndex === 0;

  const loading = useSelector(downloadMediaLoaderSelector);

  const selectedItem = useSelector(adDetailsSelector);
  const isLoadingAd = useSelector(isLoadingAdDetails);
  const adDetailsError = useSelector(isAdDetailsError);
  
  // Check if ad is manually uploaded (no libraryId indicates manual upload)
  const isManuallyUploaded = !selectedItem?.libraryId;


  useEffect(() => {
    if (libraryId) {
      dispatch(MetaDataSagaActions.fetchAdById(libraryId));
      dispatch(MetaDataSagaActions.fetchBoardList());
      dispatch(MetaDataSagaActions.fetchTagLists());
    }
  }, [libraryId, dispatch]);

  // Reset data when component unmounts
  useEffect(() => {
    return () => {
      dispatch(MetaDataReducerAction.resetAdDetailsData());
    };
  }, [dispatch]);
  useEffect(() => {
    if (!isLoadingAd && adDetailsError) {
      notify({
        message: "This ad is no longer available or has been deleted.",
        type: ToastTypes.ERROR,
      });
      navigate(routeConstant.dashboard);
    }
  }, [isLoadingAd, adDetailsError, navigate]);

  const handleDelete = () => {
    // Close modal immediately
    dispatch(SwipeFileReducerAction.setDeleteAdsModal(false));
    // Start delete process
    dispatch(SwipeFileSagaActions.deleteAd());
    // Navigate immediately
    navigate(`${routeConstant.swipeFiles}#my-ads`);
  };

  const openDeleteModal = () => {
    dispatch(SwipeFileReducerAction.setDeleteAdsModal(true));
    dispatch(SwipeFileReducerAction.setSelectedDeleteAdId(selectedItem._id));
  };

  const handleDownload = async (adId: string, page: string) => {
    dispatch(MetaDataSagaActions.downloadMediaZipFile({ adId, page }));
  };

  const handleThumbnail = (urlLink: string) => {
    const newTab = window.open("", "_blank");
    if (newTab) {
      newTab.document.write();
      newTab.document.write(
        `<body 
        style="background-color: black; 
        margin: 0;
        height: 100%;
        width: 100%;"
        >
        <img src="${urlLink}" alt="Image" style="display: block;
        position: absolute;
        height: 100%;
        width: 100%;
        /*top: 190px;*/
        /*left: 450px;*/
        margin: 0 auto;
        background-color: black;
        text-align: center;
        object-fit: contain;"/>
        </body>
        `
      );
      newTab.document.close();
    }
  };

  return isLoadingAd ? (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "60vh",
        background: `${theme.colors.gray97} !important`,
      }}
    >
      <CircularProgress size={40} color="inherit" />
    </Box>
  ) : (
    <>
      <AdDrawerHeader
        libraryId={selectedItem?.libraryId}
        dataList={selectedItem ?? []}
        adId={selectedItem?._id}
        transcriptObject={
          Array.isArray(selectedItem?.videoUrls) &&
          selectedItem.videoUrls.length > 0 &&
          !selectedItem?.transcript
        }
      />
      <StyledDivider />
      <Grid
        container
        sx={{
          background: `${theme.colors.gray97} !important`,
        }}
      >
        <AdCardSection
          item={selectedItem}
          showTranscription={Boolean(selectedItem?.transcript)}
        />
        {selectedItem?.transcript && (
          <TranscriptionSection item={selectedItem} />
        )}
        <StyledGridItem
          item
          sm={selectedItem?.transcript ? 4.5 : 5}
          sx={{
            padding: "1.25rem 1.875rem 1.875rem 1.25rem",
            gap: "1.25rem",
            flexDirection: "column",
            background: `${theme.colors.gray97} !important`,
          }}
        >
          <OrganizationBox
            isSwipeFilePage={isNotExploreLounge}
            item={selectedItem}
          />
          <MetadataBox item={selectedItem} isSwipeFilePage={isSwipeFile} />
         {user?._id === selectedItem?.userId && !(isExploreDiscovery && isManuallyUploaded) && <CommentBox item={selectedItem} />}
          <Stack direction="row" alignItems="center" gap="0.625rem">
            <AdDrawerStyledButton
              fullWidth
              onClick={() =>
                handleDownload(selectedItem?._id, selectedItem?.page?.name)
              }
              disabled={loading}
              sx={{
                backgroundColor: `${theme.colors.background4} !important`,
              }}
              startIcon={
                loading && <CircularProgress size={20} color="inherit" />
              }
            >
              Download
            </AdDrawerStyledButton>
            <AdDrawerStyledButton
              fullWidth
              sx={{
                backgroundColor: `${theme.colors.background4} !important`,
              }}
              disabled={selectedItem?.videoUrls?.length > 0}
              onClick={() => handleThumbnail(selectedItem?.imageUrls[0])}
            >
              Thumbnail
            </AdDrawerStyledButton>
          </Stack>
          { user?._id === selectedItem?.userId && (
            <AdDrawerStyledButton
              fullWidth
              borderNone={true}
              textColor="white"
              sx={{
                backgroundColor: `${theme.colors.red200} !important`,
              }}
              onClick={() => openDeleteModal()}
            >
              Delete
            </AdDrawerStyledButton>
          )}
        </StyledGridItem>
      </Grid>
      <DeleteAdModal handleDelete={handleDelete} />
      {openCreateFolder && <CreateFolderModal />}
      {openCreateTeamFolder && <CreateTeamFolderModal />}
    </>
  );
};

export default AdDetails;
